import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import {
  ActivatedRouteSnapshot,
  NavigationEnd,
  NavigationStart,
  Router,
} from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/**
 * Route `data` flag that hides the footer on a route and every route under it,
 * e.g. `data: { hideFooter: true }`. The footer belongs on marketing pages;
 * pages that take user input (forms, dashboards, sign-in) set this.
 */
export const HIDE_FOOTER_ROUTE_DATA_KEY = 'hideFooter';

@Injectable({
  providedIn: 'root',
})
export class FooterService {
  private readonly router = inject(Router);

  private isShown = new BehaviorSubject(true);

  private readonly routeHidesFooter = new BehaviorSubject(
    this.currentRouteHidesFooter(),
  );

  constructor() {
    this.router.events.pipe(takeUntilDestroyed()).subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.isShown.next(true);
      } else if (event instanceof NavigationEnd) {
        this.routeHidesFooter.next(this.currentRouteHidesFooter());
      }
    });
  }

  getVisibility(): Observable<boolean> {
    return combineLatest([this.isShown, this.routeHidesFooter]).pipe(
      map(([shown, routeHides]) => shown && !routeHides),
      distinctUntilChanged(),
    );
  }

  /**
   * Shows or hides the footer until the next navigation. A route flagged with
   * {@link HIDE_FOOTER_ROUTE_DATA_KEY} keeps it hidden regardless; prefer the
   * flag when a whole route never shows the footer.
   */
  setVisibility(isFooterVisible: boolean): void {
    this.isShown.next(isFooterVisible);
  }

  private currentRouteHidesFooter(): boolean {
    let route: ActivatedRouteSnapshot | null =
      this.router.routerState.snapshot.root;
    while (route) {
      if (route.data[HIDE_FOOTER_ROUTE_DATA_KEY] === true) return true;
      route = route.firstChild;
    }
    return false;
  }
}
